import { useState, useRef, forwardRef } from 'react'
// @mui
import { Stack, FormControlLabel, Checkbox } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import sortBy from 'lodash/sortBy'
// @types
// components
import Iconify from '@/components/Iconify'
import { TagsForAllProductsFragment } from '@/generated/graphql'
import useCollapseDrawer from '@/hooks/useCollapseDrawer'
import { ListItemStyle, PaperStyle } from '@/components/nav-section/horizontal/style'
import { HEADER } from '@/config'

const hideScrollbar = {
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
} as const

const RootStyle = styled('div')(({ theme }) => ({
  transition: theme.transitions.create('top', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  width: '100%',
  position: 'fixed',
  zIndex: theme.zIndex.appBar,
  padding: theme.spacing(1, 0),
  boxShadow: theme.customShadows.z8,
  top: HEADER.MOBILE_HEIGHT,
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.up('md')]: {
    top: HEADER.MAIN_DESKTOP_OFFSET_HEIGHT,
  },
}))

type Props = {
  filteredTagIds: string[]
  filterTags?: TagsForAllProductsFragment[]
  isOpen: boolean
  onFilterTagIds?: (tagIds: string[]) => void
  onRemoveTagId?: (tagId: string) => void
  onAddTagId?: (tagId: string) => void
  onOpen: VoidFunction
  onResetAll: VoidFunction
  onClose: VoidFunction
}

export default function ShopFilterTopBar({
  filteredTagIds,
  filterTags,
  isOpen,
  onFilterTagIds,
  onRemoveTagId,
  onAddTagId,
  onResetAll,
  onOpen,
  onClose,
}: Props) {
  const theme = useTheme()

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer()
  const groupByNameTags = groupBy(filterTags, 'groupName')

  const handleSetTag = (tag: TagsForAllProductsFragment, action: 'add' | 'remove') => {
    const url = new URL(window.location.href)
    const tagsParamsString = url.searchParams.get('tags')
    const tagSearch = `${tag.sys.id}_${tag.name}`
    let tagsIdWithNameParams: string[] = []

    if (tagsParamsString) {
      tagsIdWithNameParams = tagsParamsString.split('|').filter((tag) => {
        const tagId = tag.split('_')[0]
        return filterTags?.some((filterTag) => tagId === filterTag.sys.id) ?? false
      })
    }

    if (action === 'add') {
      tagsIdWithNameParams = uniq([...tagsIdWithNameParams, tagSearch])
      onAddTagId?.(tag.sys.id)
    } else {
      tagsIdWithNameParams = uniq(
        tagsIdWithNameParams.filter((tagIdWithName) => tagIdWithName !== tagSearch)
      )
      onRemoveTagId?.(tag.sys.id)
    }
    if (tagsIdWithNameParams.length) {
      url.searchParams.set('tags', tagsIdWithNameParams.join('|'))
    } else {
      url.searchParams.delete('tags')
    }
    window.history.replaceState(null, '', url)
  }

  return (
    <RootStyle>
      <Stack
        direction="row"
        justifyContent="center"
        sx={{ bgcolor: 'background.neutral', px: 0.5 }}
      >
        <Stack direction="row" sx={{ ...hideScrollbar, py: 1 }}>
          {sortBy(Object.entries(groupByNameTags), [([_, groupedTags]) => groupedTags.length])
            .reverse()
            .map(([groupName, groupedTags]) => (
              <Stack key={groupName || 'Others'} direction="row" flexShrink={0}>
                <ListRoot
                  isCollapse={isCollapse ?? false}
                  list={{
                    title: groupName || 'Others',
                    children: groupedTags.map((groupedTag) => ({
                      id: groupedTag.sys.id,
                      title: groupedTag.name ?? '',
                      checked: filteredTagIds.includes(groupedTag.sys.id),
                      onChecked: () => {
                        handleSetTag(
                          groupedTag,
                          filteredTagIds.includes(groupedTag.sys.id) ? 'remove' : 'add'
                        )
                      },
                    })),
                  }}
                />
              </Stack>
            ))}
        </Stack>
      </Stack>
    </RootStyle>
  )
}

type ListRootProps = {
  list: {
    title: string
    children: {
      id: string
      title: string
      checked?: boolean
      onChecked?: () => void
    }[]
  }
  isCollapse: boolean
}

export function ListRoot({ list }: ListRootProps) {
  const menuRef = useRef(null)

  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      <NavItemSub
        open={open}
        item={list}
        ref={menuRef}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
      />

      <PaperStyle
        open={open}
        anchorEl={menuRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          onMouseEnter: handleOpen,
          onMouseLeave: handleClose,
          sx: {
            mt: 1.5,
            maxHeight: 500,
            minWidth: 200,
            overflowY: 'auto',
          },
        }}
      >
        {(list.children || []).map((item) => (
          <FormControlLabel
            key={item.id}
            sx={{ display: 'block', ml: 0.5 }}
            control={
              <Checkbox size="small" checked={item.checked} onChange={() => item.onChecked?.()} />
            }
            label={item.title}
          />
        ))}
      </PaperStyle>
    </>
  )
}

type ItemProps = {
  item: ListRootProps['list']
  isCollapse?: boolean | undefined
  active?: boolean | undefined
  open?: boolean | undefined
  onOpen?: VoidFunction | undefined
  onMouseEnter?: VoidFunction | undefined
  onMouseLeave?: VoidFunction | undefined
}

export const NavItemSub = forwardRef<HTMLButtonElement & HTMLAnchorElement, ItemProps>(
  ({ item, active, open, onMouseEnter, onMouseLeave }, ref) => {
    const { title, children } = item

    return (
      <ListItemStyle
        ref={ref}
        subItem
        disableRipple
        open={open}
        activeSub={active}
        onClick={() => {
          !open && onMouseEnter?.()
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {title} ( {children.length} )
        <ArrowIcon open={open} />
      </ListItemStyle>
    )
  }
)

type ArrowIconProps = {
  open?: boolean
}

export function ArrowIcon({ open }: ArrowIconProps) {
  return (
    <Iconify
      icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
      sx={{ width: 16, height: 16, ml: 1 }}
    />
  )
}
